import { useMemo, useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import type { DashboardChartFilters, DashboardCityOption, DashboardGranularity } from '../types/dashboardStats';
import { buildDashboardDateRange, DASHBOARD_PRESETS, type PeriodMode } from '../utils/dashboardChartUtils';

export function useDashboardChartFilters() {
  const [granularity, setGranularityState] = useState<DashboardGranularity>('day');
  const [periodMode, setPeriodMode] = useState<PeriodMode>('preset');
  const [presetKey, setPresetKey] = useState('30d');
  const [customFrom, setCustomFrom] = useState<Dayjs | null>(dayjs().subtract(29, 'day'));
  const [customTo, setCustomTo] = useState<Dayjs | null>(dayjs());
  const [selectedCity, setSelectedCity] = useState<DashboardCityOption | null>(null);

  const dateRange = useMemo(
    () => buildDashboardDateRange(granularity, periodMode, presetKey, customFrom, customTo),
    [granularity, periodMode, presetKey, customFrom, customTo],
  );

  const filters: DashboardChartFilters = useMemo(
    () => ({
      granularity,
      date_from: dateRange.date_from,
      date_to: dateRange.date_to,
      city: selectedCity?.city,
    }),
    [granularity, dateRange.date_from, dateRange.date_to, selectedCity?.city],
  );

  const customRangeInvalid = Boolean(
    periodMode === 'custom' && customFrom && customTo && customFrom.isAfter(customTo, 'day'),
  );

  const setGranularity = (value: DashboardGranularity) => {
    setGranularityState(value);
    const presetList = DASHBOARD_PRESETS[value];
    setPresetKey(presetList[presetList.length - 1].key);
    setPeriodMode('preset');
  };

  return {
    filters,
    granularity,
    setGranularity,
    periodMode,
    setPeriodMode,
    presetKey,
    setPresetKey,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    selectedCity,
    setSelectedCity,
    customRangeInvalid,
  };
}
